'use client';

import { useState } from 'react';
import { PortalLink, useCart, useCommerceSource } from '@akademi/site-builder-runtime';
import { SavedListPicker, type SavedListPickerSource } from '@commerce/site-builder-blocks';

/**
 * A quantity and an Add to basket, small enough to sit on a listing card.
 *
 * <p>The reason it exists rather than reusing {@link AddToBasket}: that one is the
 * product page's, full width and quantity one. A trade buyer ordering a roof's worth
 * of clamps wants to type 48 and move on, and making them open fourteen product pages
 * to add fourteen lines is the click-per-line cost the whole trade-catalogue layout
 * exists to remove.
 *
 * <p><strong>The quantity is local state, not cart state.</strong> It is what the
 * shopper is about to add, and it resets to 1 after a successful add — so a second
 * click adds one more rather than silently repeating a bulk quantity they already
 * committed. What is already in the basket is read from the cart and shown beside the
 * control, because the number in the box and the number in the basket are different
 * facts and a card that conflates them reads as if the add did nothing.
 *
 * <p>Renders nothing when no cart is mounted, exactly as {@link AddToBasket} does: a
 * shop that sells by enquiry has no basket, and an inert button explaining that is
 * worse than the enquiry route the page already offers.
 */

/**
 * How much room the control has.
 *
 * <p>`card` is the listing card's: a 48px quantity box and a button that truncates
 * rather than widening, because two of these across a 360px phone is the constraint
 * that decides the whole card layout.
 *
 * <p>`panel` is the product page's buy box, where the control is the primary action on
 * the page and has a column to itself. It is the same component rather than a third
 * one because the part that goes wrong is the cart interaction — quantity parsing, the
 * reset after an add, reading what is already in the basket — and keeping two copies of
 * that is how they diverge.
 */
export type BasketStepperSize = 'card' | 'panel';

export function BasketStepper({
  productId,
  addLabel,
  inBasketLabel,
  quantityLabel,
  outOfStockLabel,
  errorLabel,
  cartHref = '/cart',
  outOfStock = false,
  size = 'card',
}: {
  productId: number;
  addLabel: string;
  inBasketLabel: string;
  quantityLabel: string;
  outOfStockLabel: string;
  errorLabel: string;
  cartHref?: string;
  outOfStock?: boolean;
  size?: BasketStepperSize;
}) {
  const [quantity, setQuantity] = useState('1');
  const cart = useCart();
  const commerce = useCommerceSource();
  const lists = commerce && typeof commerce === 'object' && 'buyingLists' in commerce
    ? commerce.buyingLists as SavedListPickerSource | undefined : undefined;
  const scopeKey = lists && typeof lists.list === 'function' && typeof lists.detail === 'function'
    && typeof lists.create === 'function' && typeof lists.addItems === 'function'
    && typeof lists.getScopeKey === 'function' ? lists.getScopeKey() : undefined;

  if ((!cart || !cart.available) && !scopeKey) return null;

  const inBasket =
    cart?.cart?.lines
      .filter((line) => line.productId === productId && line.parentLineId == null)
      .reduce((total, line) => total + Number(line.quantity), 0) ?? 0;

  // An empty or junk box means one, not zero and not NaN: the button says "add", so
  // the least surprising reading of a blank field is the smallest thing it could add.
  const parsed = Math.max(1, Math.floor(Number(quantity) || 1));

  async function add() {
    if (!cart) return;
    await cart.add({ productId, quantity: String(parsed) });
    setQuantity('1');
  }

  const panel = size === 'panel';

  return (
    <div className={panel ? '' : 'mt-3'}>
      {cart?.available && <div className="flex items-stretch gap-2">
        <label className="sr-only" htmlFor={`qty-${productId}`}>
          {quantityLabel}
        </label>
        <input
          id={`qty-${productId}`}
          type="number"
          inputMode="numeric"
          min={1}
          step={1}
          value={quantity}
          onChange={(event) => setQuantity(event.target.value)}
          disabled={outOfStock}
          className={`shrink-0 border border-[var(--color-border-subtle)] bg-[var(--color-surface-card)] text-center font-semibold tabular-nums focus-visible:outline-2 focus-visible:outline-offset-1 disabled:opacity-55 ${
            panel ? 'w-20 min-h-13 px-2 text-base' : 'w-12 px-1 py-2 text-sm'
          }`}
        />
        <button
          type="button"
          onClick={() => void add()}
          disabled={cart.busy || outOfStock}
          // `min-w-0` is load-bearing, not tidiness. A flex item defaults to
          // `min-width: auto`, so this button refuses to shrink below the width of
          // "Add to basket" — and two of these in a two-column grid at 360px make the
          // page wider than the viewport. That does not look like a card bug: the shell's
          // nav collapses to icon-only below 1023px, so the first thing anyone notices is
          // a header whose labels vanished on the listing page and not on the home page.
          className={`min-w-0 flex-1 truncate bg-[var(--color-brand-600)] font-bold text-[var(--color-text-on-brand)] transition-[filter] hover:brightness-110 disabled:opacity-55 focus-visible:outline-2 focus-visible:outline-offset-2 ${
            panel ? 'min-h-13 px-6 text-base' : 'min-h-10 px-2 text-sm'
          }`}
        >
          {outOfStock ? outOfStockLabel : addLabel}
        </button>
      </div>}
      {inBasket > 0 ? (
        <PortalLink
          href={cartHref}
          className={`inline-block font-medium underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 ${
            panel ? 'mt-2.5 text-sm' : 'mt-1.5 text-xs'
          }`}
        >
          {inBasketLabel} ({inBasket})
        </PortalLink>
      ) : null}
      {cart?.error ? (
        <p
          role="alert"
          className={`text-[var(--color-danger-600,#dc2626)] ${panel ? 'mt-2.5 text-sm' : 'mt-1.5 text-xs'}`}
        >
          {errorLabel}
        </p>
      ) : null}
      {scopeKey && lists && <div className={panel ? 'mt-3' : 'mt-2'}>
        <SavedListPicker key={`${scopeKey}:${productId}`} source={lists} scopeKey={scopeKey}
          product={{ id: String(productId), title: 'Product' }} initialQuantity={String(parsed)} />
      </div>}
    </div>
  );
}
